import { Component, Input, OnChanges, OnInit } from "@angular/core";
import { getValuesFromEnum } from "@app/class/Utilities";
import { DeviceCommunicationType } from "@app/enum/deviceMessage/DeviceCommunicationType";
import { DistanceMeasurement } from "@app/enum/measurements/DistanceMeasurement.enum";
import { IDeviceCommand, IDeviceCommunicationCommand } from "@app/interfaces/IDeviceMessage";
import { UnitLocation } from "@app/main/modules/foundation/_enums/UnitLocations";
import { ISerialSearchResponse } from "@app/main/modules/foundation/_models/ISerialSearchResponse";
import { ValidationCheckType } from "@app/main/modules/foundation/_services/device-data-validation.service";
import { DeviceMessageService } from "@app/main/modules/foundation/_services/deviceMessage.service";
import { FoundationVehicleService } from "@app/main/modules/foundation/_services/vehicle.service.ajs";
import { TranslationService } from "@app/services/translation.service";
import { ProductVendor } from "@components/videos/enums/CameraProductType";
import { VButtonStatus } from "@directives/_forms/v-input/v-input.component";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { CommissionDurationPeriods, EngineerJobItemType, NonTestableDevices, ValidationResult } from "@foundation/foundation.enums";
import { IDeviceToAdd, IEngineerCommission, IValidationResultCategory, IValidationResults } from "@foundation/foundation.interfaces";
import { CommissionService } from "@foundation/_routes/commissions/commission.service";
import { ManualDeviceValidationCheckService } from "@foundation/_services/manual-device-validation-check.service";
import { FoundationPeripheralService } from "@foundation/_services/peripheral.service.ajs";
import { IKeyValuePair } from "@interfaces/IKeyValuePair";
import { IUnit, SavedState } from "@interfaces/IPeripheral";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EnumService } from "@services/enum.service";
import { Observable, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";
import "./device-install-editor.component.scss";

@UntilDestroy()
@Component({
    selector: "foundation-device-install-editor",
    template: require('./device-install-editor.component.html')
})
export class DeviceInstallEditor implements OnInit, OnChanges {

    // bindings

    @Input() commission: IEngineerCommission;

    // internal

    loading = false;

    serial: string;
    devicesUpdated: boolean = false;
    deviceToAdd: IDeviceToAdd;
    devicesInstalled: IDeviceToAdd[];

    existingDeviceToValidate: IDeviceToAdd;
    existingDeviceUnitToValidate: IUnit;

    pendingUnitChanges: IUnit[];

    allowAddButton = false;
    allowRemoveButton = false;
    showAddDevice = false;
    showBarcodeScanner = false;
    showExistingHardwareValidation = false;

    checkResults: IValidationResultCategory[] = [];

    ptoActive: boolean = false;
    peripheralNoTrackerError = false;
    unitSupportsTemperatureProbes: boolean = false;
    includeTemperatureTests: boolean = false;

    nonTestableDevice: boolean = false;
    nonTestableDeviceTypesAdded: NonTestableDevices = NonTestableDevices.DVS;

    // enums
    DurationPeriod = CommissionDurationPeriods;
    NonTestableDevices: IKeyValuePair<typeof NonTestableDevices>[] = [];

    validationResults: IValidationResults;
    requiresUnitLocation: boolean = false;

    editGpsOdo: boolean = false;

    currentOdoKm: number = 0;
    currentOdoMiles: number = 0;
    DistanceMeasurement = DistanceMeasurement;
    distanceMeasurementOptions = Object.keys(DistanceMeasurement).filter(s => !isNaN(Number(s))).map(s => Number(s));
    distanceMeasurement: DistanceMeasurement = DistanceMeasurement.Miles;
    distanceRaw: number = 0;
    distanceInMetres: number = 0;
    odoSetCommand: IDeviceCommunicationCommand;
    milesConversionConstant = 1609.344;
    kilometreConversionConstant = 1000;

    serialSearch: Subscription;
    preselectableLocations = getValuesFromEnum(UnitLocation).map(value => this.translationService.tEnum(UnitLocation, "UnitLocation", value));
    UnitLocation = UnitLocation;

    constructor(
        private readonly manualDeviceValidationCheckService: ManualDeviceValidationCheckService,
        private readonly commissionService: CommissionService,
        private readonly foundationPeripheralService: FoundationPeripheralService,
        private readonly foundationVehicleService: FoundationVehicleService,
        private readonly translationService: TranslationService,
        private readonly deviceMessageService: DeviceMessageService) {

        this.resetDeviceToAdd();
    }

    // Todo
    // Add temp to all tracking units (not obd?), not gl500
    // Check box on next page confirming everything, so tick for all?

    ngOnInit(): void {
        this.createnonTestDeviceTypes();
    }

    ngOnChanges(): void {
        this.resetDeviceToAdd();
        this.getSelectedVehiclePeripherals();

        switch (this.commission.VisitReasonId) {
            case EngineerJobItemType.Install:
            case EngineerJobItemType.Reinstall:
                this.allowAddButton = true;
                this.allowRemoveButton = false;
                break;

            case EngineerJobItemType.Uninstall:
                this.allowRemoveButton = true;
                this.allowAddButton = false;
                break;

            case EngineerJobItemType.ServiceCall:
                this.allowAddButton = true;
                this.allowRemoveButton = true;

            default:
                this.allowAddButton = true;
                this.allowRemoveButton = true;
        }

        if (!this.commission.Peripherals) {
            this.commission.Peripherals = [];
        }

    }

    get canRunTemperatureTests(): boolean {
        return !!this.deviceToAdd?.searchResponse?.Unit;
    }

    get showGpsOdo(): boolean {
        return this.deviceToAdd?.searchResponse?.Product?.IsTracker;
    }

    get isServiceCall(): boolean {
        return this.commission.VisitReasonId == EngineerJobItemType.ServiceCall;
    }

    get disableAdd(): boolean {
        return this.deviceToAdd?.buttonStatus != VButtonStatus.Success || (!this.commission.vehicle?.UnitLocation?.length && this.deviceToAdd.searchResponse?.Product?.IsTracker);
    }

    get removedDevicesExist(): boolean {
        return this.commission.pendingCommissionChanges.some(d => d.Unit.savedState == SavedState.RemovedRequiresSave);
    }

    get formIsValid(): boolean {
        return (!this.commission.Chargeable
            || (this.commission.Chargeable && this.commission.ChargeReason?.length > 0))
            && this.devicesUpdated;
    }

    onDevicechanged(deviceType: string): void {
        this.nonTestableDeviceTypesAdded = NonTestableDevices[deviceType];
    }

    onSimUpdated(simId: number): void {
        this.devicesUpdated = true;
    }

    addNonTestableDevice(peripheral: NonTestableDevices): void {
        this.deviceToAdd = {
            serial: "",
            inputStatus: VButtonStatus.Default,
            buttonStatus: VButtonStatus.Default
        };

        this.deviceToAdd.manualCategories = (this.manualDeviceValidationCheckService.createPeripheralTestCategories(peripheral));
    }

    serialChanged(serial: string): void {
        if (this.deviceToAdd.serial == serial) {
            return;
        }

        this.deviceToAdd.buttonStatus = VButtonStatus.Default;
        this.deviceToAdd.serial = serial;

        if (this.deviceToAdd.serial == null) {
            this.deviceToAdd.searchResponse = null;
            return;
        }

        if (!this.deviceToAdd.searchResponse) {
            this.deviceToAdd.searchResponse = { parentTrackerImei: this.commission?.Imei }
            return;
        }

        this.deviceToAdd.searchResponse.parentTrackerImei = this.commission?.Imei;
    }

    onBarcodeSelected(barcode): void {
        this.showBarcodeScanner = false;
        this.serial = barcode;
        this.serialChanged(this.serial);
    }

    onBarcodeScanned(barcode): void {
        this.serialSearch?.unsubscribe();

        this.serialSearch = this.searchForSerial(barcode).pipe(
            untilDestroyed(this),
            tap(response => {
                if (!response || Object.keys(response).length === 0) {
                    return;
                }

                this.onBarcodeSelected(barcode)
            })).subscribe();
    }

    onUnitLocationChange(location: string): void {
        this.commission.vehicle.UnitLocation = location;
        this.commission.UnitLocation = location;
    }

    onValidationComplete(validationResult: IValidationResults): void {
        this.validationResults = validationResult;

        this.validationResults.checksRun.forEach(cr => {
            this.commission.deviceValidationCheckRecordIds.push(cr.ValidationResultDatabaseId);
        });

        this.deviceToAdd.buttonStatus = validationResult.result === ValidationResult.Fail ? VButtonStatus.Error : VButtonStatus.Success;

        const plotCheck = this.validationResults.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.Plot);

        if (!plotCheck || !plotCheck.TestData || (this.deviceToAdd.buttonStatus == VButtonStatus.Error)) {
            return;
        }

        this.currentOdoKm = +(plotCheck.TestData[plotCheck.TestData.length - 1].Odometer / this.kilometreConversionConstant).toFixed(1);
        this.currentOdoMiles = +(plotCheck.TestData[plotCheck.TestData.length - 1].Odometer / this.milesConversionConstant).toFixed(1);

        if (!!this.deviceToAdd?.searchResponse?.Unit) {
            this.deviceMessageService.getDeviceCommunicationCommands(this.deviceToAdd.searchResponse.Unit.Serial, false).pipe(take(1), tap(deviceCommands => {
                this.odoSetCommand = deviceCommands.find(m => m.Item2 == DeviceCommunicationType.SetOdometer) ?? null;
            }));
        }
    }

    validatePreAssociatedDevice(unit: IUnit): void {

        this.existingDeviceToValidate = {
            serial: unit.Serial,
            buttonStatus: VButtonStatus.Default,
            inputStatus: VButtonStatus.Default,
            searchResponse: { parentTrackerImei: this.commission.Imei }
        }
        this.showExistingHardwareValidation = true;

        this.existingDeviceUnitToValidate = unit;
    }

    toggleRemovePeripheral(unit: IUnit): void {

        if (unit.savedState == SavedState.NoSaveRequired) {
            unit.savedState = SavedState.RemovedRequiresSave;
            this.addUnitChangedComment(unit, "Removed");
        }
        else if (unit.savedState == SavedState.AddedRequiresSave) {

            this.commission.pendingCommissionChanges = this.commission.pendingCommissionChanges.filter(u => u.Unit != unit);

            unit.savedState = SavedState.NoSaveRequired;
        }
        else {
            unit.savedState = SavedState.NoSaveRequired;
        }

        if (unit.savedState == SavedState.NoSaveRequired) {
            this.removeUnitChangedComment(unit);
        }

        if (unit.Serial == this.commission.Serial) {
            this.commission.Serial = "";
            this.commission.UnitLocation = "";
        }

        if (unit.savedState == SavedState.RemovedRequiresSave && unit.Serial == this.commission.CameraId) {

            this.commission.dirtyCameraId = this.commission.CameraId;
            this.commission.CameraId = "";
        }

        if (unit.savedState == SavedState.NoSaveRequired && (unit.Serial == this.commission.CameraId || unit.Serial == this.commission.dirtyCameraId)) {

            this.commission.CameraId = this.commission.dirtyCameraId;
            this.commission.dirtyCameraId = '';
        }

        this.commission.Peripherals = this.commission.Peripherals.filter(p => p != unit.Serial);
        this.setAnyDevicesUpdated();
        this.updateInstallationState();
    }

    toggleEditGpsOdo(event: boolean): void {
        if (!event) {
            this.distanceRaw = 0;
        }
        this.editGpsOdo = event;
    }

    onSetDistanceMeasurement(event: any): void {
        this.distanceMeasurement = event;
    }

    setGpsOdo(): void {
        const deviceCommand: IDeviceCommand = {
            CommandValue: (Math.round(this.distanceRaw * (this.distanceMeasurement == DistanceMeasurement.Kilometres ? this.kilometreConversionConstant : this.milesConversionConstant))).toString(),
            CommunicationMethod: this.odoSetCommand.Item1,
            CommunicationType: this.odoSetCommand.Item2
        }   

        this.deviceMessageService.sendDeviceCommunicationCommands(this.deviceToAdd.serial, deviceCommand);
    }

    togglePtoActive(): void {
        this.ptoActive = !this.ptoActive;
    }

    toggleAddDeviceView(): void {
        this.showAddDevice = !this.showAddDevice;
    }

    back(): void {
        if (this.showBarcodeScanner) {
            this.showBarcodeScanner = false;
        } else if (this.deviceToAdd.manualCategories) {
            this.deviceToAdd.manualCategories = null;
        } else if (this.nonTestableDevice) {
            this.nonTestableDevice = false;
        } else if (this.showExistingHardwareValidation) {
            this.showExistingHardwareValidation = false;
            this.resetDeviceToAdd();
        } else {
            this.resetDeviceToAdd();
        }
    }

    resetDeviceToAdd(): void {
        this.showAddDevice = false;
        this.nonTestableDevice = false;
        this.deviceToAdd = {
            serial: "",
            inputStatus: VButtonStatus.Default,
            buttonStatus: VButtonStatus.Default,
            manualCategories: null
        };
        this.existingDeviceToValidate = null;
        this.validationResults = null;
        this.editGpsOdo = false;
        this.distanceRaw = 0;

        if (!!this.commission?.Metadata) {
            this.commission.Metadata.NoFaultFound = false;
        }


    }

    addDevice(): void {

        if (this.deviceToAdd.serial) { // Any automated checks should run through this
            var newPeripheral = this.createUnitFromSerial(this.deviceToAdd.serial, this.deviceToAdd.searchResponse.Unit?.Imei ?? this.deviceToAdd.searchResponse.Peripheral?.Imei, this.deviceToAdd.searchResponse.primaryFeature);

            if (this.deviceToAdd.searchResponse.Unit) {
                this.commission.Serial = this.deviceToAdd.searchResponse.Unit.Serial;
                this.commission.Imei = this.deviceToAdd.searchResponse.Unit.Imei;

            }
            else {

                var features = this.foundationPeripheralService.findFeaturesFromFeaturesEnum(this.deviceToAdd.searchResponse.Product.Features);

                if (features.indexOf(AccountFeatures.Camera) > -1) {
                    this.commission.dirtyCameraId = this.commission.CameraId;
                    this.commission.CameraId = this.deviceToAdd.searchResponse.Serial;
                }

                if (this.deviceToAdd.searchResponse.Product.GroupId === ProductVendor.Squarell) {
                    this.commission.CanbusId = this.deviceToAdd.serial;
                }

                this.commission.Peripherals.push(this.deviceToAdd.searchResponse.Peripheral.Serial);
            }

            this.addUnitChangedComment(newPeripheral, "Added");



            this.commission.pendingCommissionChanges.push({
                Unit: newPeripheral,
                //CommissionResults: this.checkResults,
                faultFlagged: this.deviceToAdd.faultRecorded,
                faultComments: this.deviceToAdd.faultComments,
                isCamera: features?.indexOf(AccountFeatures.Camera) > -1
            });
        } else { // These are units that are not connected to Verilocation, but need documenting as installed (Standalone units).
            this.commission.Peripherals.push("DVS");

            this.commission.pendingCommissionChanges.push({
                Unit: {
                    name: `${NonTestableDevices[this.deviceToAdd.manualCategories.section]} - No Remote Connection`,
                    icon: "link_off",
                    savedState: SavedState.AddedRequiresSave
                },
                //CommissionResults: this.checkResults,
                faultFlagged: !!this.deviceToAdd.manualCategories.faultComments,
                faultComments: this.deviceToAdd.manualCategories.faultComments
            });
            this.commission.Comments += `Standalone ${NonTestableDevices[this.deviceToAdd.manualCategories.section]} unit Added \r\n`;
        }

        this.resetDeviceToAdd();
        this.setupUnits();
        this.setAnyDevicesUpdated();
        this.updateInstallationState();
    }


    updateInstallationState(): void {
        const hasAdded = this.commission.pendingCommissionChanges.some(p => p.Unit.savedState == SavedState.AddedRequiresSave);
        const hasRemoved = this.commission.pendingCommissionChanges.some(p => p.Unit.savedState == SavedState.RemovedRequiresSave);

        this.devicesUpdated = (hasAdded || hasRemoved) || (this.commission.Metadata?.NoFaultFound && this.commission.existingHardwareAcquired);
    }

    onNoFaultFoundChecked(event: any): void {
        if (!this.commission.Metadata) {
            this.commission.Metadata = {}
        }

        this.commission.Metadata.NoFaultFound = event;
        this.allowAddButton = this.allowRemoveButton = !event;

        this.updateInstallationState();
    }

    onIncludeTemperatureProbeChange(event: any): void {
        this.includeTemperatureTests = event;
    }

    // ---

    private createnonTestDeviceTypes(): void {

        this.NonTestableDevices = EnumService.enumToArray("NonTestableDevices", NonTestableDevices);

        this.NonTestableDevices.sort((type1, type2) => (type1.name > type2.name) ? 1 : -1);
    }

    private setAnyDevicesUpdated(): void {
        this.devicesUpdated = this.commission.pendingCommissionChanges.some(change => change.Unit.savedState != SavedState.NoSaveRequired);
    }


    private createUnitFromSerial(serial: string, imei: string, primaryFeature = AccountFeatures.GPS): IUnit {
        const unit: IUnit = {
            Serial: serial,
            icon: this.foundationPeripheralService.getIconForFeature(primaryFeature),
            savedState: SavedState.AddedRequiresSave,
            primaryFeature: primaryFeature,
            Imei: imei
        };

        return unit;
    }


    private convertQPromiseIntoNativePromise<T>(ajsPromise: angular.IPromise<T>): Promise<T> {
        const promise = new Promise<T>((resolve, reject) => {

            ajsPromise.then(data => { resolve(data); })
            ajsPromise.catch(error => { reject(error); });
        });

        return promise;
    }

    private getSelectedVehiclePeripherals(): angular.IPromise<void> {
        this.commission.pendingCommissionChanges = [];
        if (!this.commission.VehicleId) {
            return;
        }

        const peripheralPromise = this.foundationPeripheralService.getPeripherals(this.commission.VehicleId, true);
        const unitPromise = this.foundationPeripheralService.getUnit(this.commission.VehicleId, true);
        const unitLocationPromise = this.foundationVehicleService.getVehicleUnitLocation(this.commission.VehicleId)

        Promise.all([this.convertQPromiseIntoNativePromise(peripheralPromise), this.convertQPromiseIntoNativePromise(unitPromise), this.convertQPromiseIntoNativePromise(unitLocationPromise)])
            .then(([peripherals, unitPromise, unitLocation]) => {

                if (unitPromise) {

                    this.commission.Serial = unitPromise.Serial;
                    this.commission.Imei = unitPromise.Imei;
                    const unit = this.createUnitFromSerial(this.commission.Serial, unitPromise.Imei);
                    unit.savedState = SavedState.NoSaveRequired;
                    unit.Id = unitPromise.Id;
                    unit.primaryFeature = AccountFeatures.TrackingDevice;
                    unit.unitLocation = unitLocation.trim().length ? unitLocation : null;

                    this.commission.pendingCommissionChanges.push({ Unit: unit });
                }

                peripherals.forEach(peripheral => this.commission.pendingCommissionChanges.push({
                    Unit: peripheral
                }));

                peripherals.forEach(p => {
                    const features = this.foundationPeripheralService.findFeaturesFromFeaturesEnum(p.Product.Features);
                    if (features.indexOf(AccountFeatures.Camera) > -1) {
                        this.commission.CameraId = p.Serial;
                    }
                });

                this.commission.Peripherals = peripherals.map(p => p.Serial);
                this.commission.existingHardwareAcquired = true;
                this.commission.UnitLocation = unitLocation;
                this.requiresUnitLocation = (!this.commission.UnitLocation && !this.commission.vehicle.UnitLocation) && !!this.commission.Serial;
                this.updateInstallationState();
            })
            .finally(() => {
                this.setupUnits();
            });
    }

    private setupUnits(): void {

        this.commission.pendingCommissionChanges.forEach(u => {
            if (!u.Unit.savedState) {
                u.Unit.savedState = SavedState.NoSaveRequired;
            }
        });
    }

    private addUnitChangedComment(unit: IUnit, verb: "Added" | "Removed"): void {
        if (this.commission.Comments.lastIndexOf("\n") + 1 < this.commission.Comments.length) {
            this.commission.Comments += "\n";
        }

        this.commission.Comments += `${verb} ${unit.name || ''}: ${unit.Serial}\r\n`
    }

    private removeUnitChangedComment(unit: IUnit): void {
        let lines = this.commission.Comments.split("\n");

        if (unit.Serial) {
            lines = lines.filter(l => (l.indexOf(unit.Serial)) < 0);
        } else {
            lines = lines.filter(l => l.indexOf(unit.name.substring(0, unit.name.indexOf(' '))) < 0);
        }

        this.commission.Comments = lines.join("\n");
    }

    private searchForSerial(serial: string, showLoader = true): Observable<ISerialSearchResponse> {

        this.loading = showLoader;

        return this.commissionService.getUnitOrPeripheralBySerial(serial);
    }
}
